import { useCallback, useMemo, useState } from "react";
import { STATUS, useJoyride } from "react-joyride";
import type { EventData, Step } from "react-joyride";

export const HOME_JOYRIDE_MENU_TARGET = '[data-tour="functionalities-menu"]';
export const HOME_JOYRIDE_PROJECTIONS_PANEL_TARGET =
  '[data-tour="projections-panel"]';
export const HOME_JOYRIDE_BASE_MAP_PANEL_TARGET =
  '[data-tour="base-map-panel"]';
export const HOME_JOYRIDE_LAYERS_PANEL_TARGET = '[data-tour="layers-panel"]';
export const HOME_JOYRIDE_DRAWER_BODY_TARGET = '[data-tour="drawer-body"]';
export const HOME_JOYRIDE_TECHNICAL_DATA_PANEL_TARGET =
  '[data-tour="technical-data-panel"]';
export const HOME_JOYRIDE_LEGENDS_PANEL_TARGET = '[data-tour="legends-panel"]';
export const HOME_JOYRIDE_REPORT_MODAL_TARGET = '[data-tour="report-modal"]';
export const HOME_JOYRIDE_TOOLS_MENU_TARGET = '[data-tour="tools-menu"]';

const DRAWER_PANEL_WAIT_MS = 350;
const DRAWER_PANEL_SPOTLIGHT_PADDING = 0;
const STATISTICS_PANEL_WAIT_MS = 350;
const REPORT_MODAL_WAIT_MS = 350;
const TOOLS_MENU_WAIT_MS = 350;

export const HOME_JOYRIDE_WELCOME_STEP_PARAGRAPHS = [
  "A Plataforma Energética de Pernambuco fornece acesso a dados, indicadores e informações estratégicas sobre o setor energético do estado, apoiando análises e planejamento de empreendimentos.",
  "Comece a explorar a plataforma clicando em “próximo” para acompanhar este tutorial autoguiado.",
];

export const HOME_JOYRIDE_WELCOME_STEP_CONTENT = (
  <div>
    {HOME_JOYRIDE_WELCOME_STEP_PARAGRAPHS.map((paragraph) => (
      <p key={paragraph} style={{ textAlign: "center" }}>
        {paragraph}
      </p>
    ))}
  </div>
);

export const HOME_JOYRIDE_WELCOME_STEP: Step = {
  target: "body",
  title: "Bem-vindo à Plataforma Energética de Pernambuco",
  content: HOME_JOYRIDE_WELCOME_STEP_CONTENT,
  placement: "center",
  skipBeacon: true,
};

export const HOME_JOYRIDE_FIRST_STEP: Step = {
  target: HOME_JOYRIDE_MENU_TARGET,
  title: "Menu de funcionalidades",
  content:
    "Através do Menu, você pode acessar as principais funcionalidades do portal, como visualização de Camadas, Projeções e Estatísticas, além de ferramentas para criação de Relatórios, Medição, Importação de arquivos e Zoom.",
  placement: "right",
  skipBeacon: true,
  spotlightPadding: 10,
  targetWaitTimeout: 1500,
};

export const HOME_JOYRIDE_LAYERS_STEP_CONTENT =
  "Camadas são conjuntos de informações exibidos sobre o mapa. Visualize diferentes dados e elementos geográficos de forma interativa. Você pode ativar, desativar e combinar camadas para personalizar sua análise e explorar diferentes perspectivas do território.";

export const HOME_JOYRIDE_SECOND_STEP_BASE: Step = {
  target: HOME_JOYRIDE_LAYERS_PANEL_TARGET,
  spotlightTarget: HOME_JOYRIDE_LAYERS_PANEL_TARGET,
  title: "Camadas",
  content: HOME_JOYRIDE_LAYERS_STEP_CONTENT,
  placement: "right",
  scrollTarget: HOME_JOYRIDE_DRAWER_BODY_TARGET,
  skipScroll: false,
  skipBeacon: true,
  spotlightPadding: DRAWER_PANEL_SPOTLIGHT_PADDING,
  targetWaitTimeout: 2500,
};

export const HOME_JOYRIDE_PROJECTIONS_STEP_PARAGRAPHS = [
  "Projeções são estimativas do potencial energético, definidos a partir da combinação de camadas e seus níveis diferentes de restrições.",
  "Você pode consultar a página de Dúvidas para entender cada estimativa em mais detalhes.",
];

export const HOME_JOYRIDE_PROJECTIONS_STEP_CONTENT = (
  <div>
    {HOME_JOYRIDE_PROJECTIONS_STEP_PARAGRAPHS.map((paragraph) => (
      <p key={paragraph} style={{ margin: "0 0 12px", textAlign: "left" }}>
        {paragraph}
      </p>
    ))}
  </div>
);

export const HOME_JOYRIDE_THIRD_STEP_BASE: Step = {
  target: HOME_JOYRIDE_PROJECTIONS_PANEL_TARGET,
  spotlightTarget: HOME_JOYRIDE_PROJECTIONS_PANEL_TARGET,
  title: "Projeções",
  content: HOME_JOYRIDE_PROJECTIONS_STEP_CONTENT,
  placement: "right",
  scrollTarget: HOME_JOYRIDE_DRAWER_BODY_TARGET,
  skipScroll: false,
  skipBeacon: true,
  spotlightPadding: DRAWER_PANEL_SPOTLIGHT_PADDING,
  targetWaitTimeout: 2500,
};

export const HOME_JOYRIDE_BASE_MAP_STEP_CONTENT =
  "Em Mapa Base, você pode definir o estilo de mapa que deseja visualizar.";

export const HOME_JOYRIDE_FOURTH_STEP_BASE: Step = {
  target: HOME_JOYRIDE_BASE_MAP_PANEL_TARGET,
  spotlightTarget: HOME_JOYRIDE_BASE_MAP_PANEL_TARGET,
  title: "Mapa Base",
  content: HOME_JOYRIDE_BASE_MAP_STEP_CONTENT,
  placement: "right",
  scrollTarget: HOME_JOYRIDE_DRAWER_BODY_TARGET,
  skipScroll: false,
  skipBeacon: true,
  spotlightPadding: DRAWER_PANEL_SPOTLIGHT_PADDING,
  targetWaitTimeout: 2500,
};

export const HOME_JOYRIDE_TECHNICAL_DATA_STEP_PARAGRAPHS = [
  "Utilize o painel de Dados Técnicos para explorar dados e indicadores sobre o potencial solar e eólico de Pernambuco.",
  "Visualize métricas, compare regiões e apoie análises sobre oportunidades de geração de energia renovável.",
  "Para entender como o cálculo de potencial é realizado, consulte a página de Dúvidas.",
];

export const HOME_JOYRIDE_TECHNICAL_DATA_STEP_CONTENT = (
  <div>
    {HOME_JOYRIDE_TECHNICAL_DATA_STEP_PARAGRAPHS.map((paragraph) => (
      <p key={paragraph} style={{ margin: "0 0 12px", textAlign: "left" }}>
        {paragraph}
      </p>
    ))}
  </div>
);

export const HOME_JOYRIDE_FIFTH_STEP_BASE: Step = {
  target: HOME_JOYRIDE_TECHNICAL_DATA_PANEL_TARGET,
  title: "Dados Técnicos",
  content: HOME_JOYRIDE_TECHNICAL_DATA_STEP_CONTENT,
  placement: "left",
  skipBeacon: true,
  spotlightPadding: 10,
  targetWaitTimeout: 2500,
};

export const HOME_JOYRIDE_LEGENDS_STEP_CONTENT =
  "Em Legendas, você pode consultar a iconografia da plataforma, identificando as camadas representadas por cada símbolo.";

export const HOME_JOYRIDE_SIXTH_STEP_BASE: Step = {
  target: HOME_JOYRIDE_LEGENDS_PANEL_TARGET,
  title: "Legendas",
  content: HOME_JOYRIDE_LEGENDS_STEP_CONTENT,
  placement: "left",
  skipBeacon: true,
  spotlightPadding: 10,
  targetWaitTimeout: 2500,
};

export const HOME_JOYRIDE_REPORT_STEP_PARAGRAPHS = [
  "Em Relatório, você pode exportar os dados visualizados no mapa, juntamente com as informações de Estatísticas e Legendas.",
  "Caso deseje exportar apenas os dados de uma área específica, selecione a área em Marcações.",
];

export const HOME_JOYRIDE_REPORT_STEP_CONTENT = (
  <div>
    {HOME_JOYRIDE_REPORT_STEP_PARAGRAPHS.map((paragraph) => (
      <p key={paragraph} style={{ margin: "0 0 12px", textAlign: "left" }}>
        {paragraph}
      </p>
    ))}
  </div>
);

export const HOME_JOYRIDE_SEVENTH_STEP_BASE: Step = {
  target: HOME_JOYRIDE_REPORT_MODAL_TARGET,
  title: "Relatório",
  content: HOME_JOYRIDE_REPORT_STEP_CONTENT,
  placement: "right",
  skipBeacon: true,
  spotlightPadding: 10,
  targetWaitTimeout: 2500,
};

export const HOME_JOYRIDE_TOOLS_STEP_PARAGRAPHS = [
  "Aqui, você pode acessar ferramentas que auxiliam na análise de informações no mapa, como marcar locais, medir distâncias, delimitar áreas, realizar desenhos e importar arquivos nos formatos KML, KMZ ou Shapefile.",
  "Ao delimitar uma área no mapa, o painel de Estatísticas é atualizado automaticamente com os dados da área selecionada.",
];

export const HOME_JOYRIDE_TOOLS_STEP_CONTENT = (
  <div>
    {HOME_JOYRIDE_TOOLS_STEP_PARAGRAPHS.map((paragraph) => (
      <p key={paragraph} style={{ margin: "0 0 12px", textAlign: "left" }}>
        {paragraph}
      </p>
    ))}
  </div>
);

export const HOME_JOYRIDE_EIGHTH_STEP_BASE: Step = {
  target: HOME_JOYRIDE_TOOLS_MENU_TARGET,
  title: "Ferramentas do Mapa",
  content: HOME_JOYRIDE_TOOLS_STEP_CONTENT,
  placement: "right",
  skipBeacon: true,
  spotlightPadding: 10,
  targetWaitTimeout: 2500,
};

interface HomeJoyrideProps {
  openProjectionsPanel: () => void;
  openBaseMapPanel: () => void;
  openLayersPanel: () => void;
  openStatisticsPanel: () => void;
  openLegendsPanel: () => void;
  openReportModal: () => void;
  openToolsMenu: () => void;
  closeToolsMenu: () => void;
}

function waitForDrawerPanel() {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, DRAWER_PANEL_WAIT_MS);
  });
}

function waitForNextPaint() {
  return new Promise<void>((resolve) => {
    if (typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(() => resolve());
      return;
    }

    window.setTimeout(resolve, 0);
  });
}

async function waitForDrawerPanelLayout() {
  await waitForDrawerPanel();
  await waitForNextPaint();
}

function waitForStatisticsPanel() {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, STATISTICS_PANEL_WAIT_MS);
  });
}

function waitForReportModal() {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, REPORT_MODAL_WAIT_MS);
  });
}

function waitForToolsMenu() {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, TOOLS_MENU_WAIT_MS);
  });
}

export function HomeJoyride({
  openProjectionsPanel,
  openBaseMapPanel,
  openLayersPanel,
  openStatisticsPanel,
  openLegendsPanel,
  openReportModal,
  openToolsMenu,
  closeToolsMenu,
}: HomeJoyrideProps) {
  const [run, setRun] = useState(true);

  const steps = useMemo<Step[]>(
    () => [
      HOME_JOYRIDE_WELCOME_STEP,
      HOME_JOYRIDE_FIRST_STEP,
      {
        ...HOME_JOYRIDE_SECOND_STEP_BASE,
        before: async () => {
          openLayersPanel();
          await waitForDrawerPanelLayout();
        },
      },
      {
        ...HOME_JOYRIDE_THIRD_STEP_BASE,
        before: async () => {
          openProjectionsPanel();
          await waitForDrawerPanelLayout();
        },
      },
      {
        ...HOME_JOYRIDE_FOURTH_STEP_BASE,
        before: async () => {
          openBaseMapPanel();
          await waitForDrawerPanelLayout();
        },
      },
      {
        ...HOME_JOYRIDE_FIFTH_STEP_BASE,
        before: async () => {
          openStatisticsPanel();
          await waitForStatisticsPanel();
        },
      },
      {
        ...HOME_JOYRIDE_SIXTH_STEP_BASE,
        before: async () => {
          openLegendsPanel();
          await waitForStatisticsPanel();
        },
      },
      {
        ...HOME_JOYRIDE_SEVENTH_STEP_BASE,
        before: async () => {
          openReportModal();
          await waitForReportModal();
        },
      },
      {
        ...HOME_JOYRIDE_EIGHTH_STEP_BASE,
        before: async () => {
          openToolsMenu();
          await waitForToolsMenu();
        },
      },
    ],
    [
      openBaseMapPanel,
      openLayersPanel,
      openLegendsPanel,
      openProjectionsPanel,
      openReportModal,
      openStatisticsPanel,
      openToolsMenu,
    ]
  );

  const finishTour = useCallback(() => {
    setRun(false);
  }, []);

  const handleEvent = useCallback(
    (data: EventData) => {
      if (data.status === STATUS.FINISHED) {
        closeToolsMenu();
        finishTour();
      }

      if (data.status === STATUS.SKIPPED) {
        finishTour();
      }
    },
    [closeToolsMenu, finishTour]
  );

  const { Tour } = useJoyride({
    continuous: true,
    run,
    scrollToFirstStep: true,
    steps,
    locale: {
      back: "Voltar",
      close: "Fechar",
      last: "Concluir",
      next: "Próximo",
      skip: "Pular Tutorial",
    },
    options: {
      blockTargetInteraction: true,
      buttons: ["skip", "primary"],
      overlayColor: "rgba(0, 0, 0, 0.55)",
      primaryColor: "#0C2856",
      spotlightPadding: 10,
      spotlightRadius: 4,
      textColor: "#1C1D20",
      width: 380,
      zIndex: 10000,
    },
    styles: {
      tooltip: {
        borderRadius: 8,
      },
      tooltipTitle: {
        color: "#0C2856",
        fontSize: 16,
        fontWeight: 700,
      },
      tooltipContent: {
        fontSize: 14,
        lineHeight: 1.5,
        textAlign: "left",
      },
      buttonPrimary: {
        borderRadius: 4,
      },
      buttonSkip: {
        color: "#61656D",
      },
    },
    onEvent: handleEvent,
  });

  return <>{Tour}</>;
}

export default HomeJoyride;
